import { formatDisplayTitle } from '../formatProductTitle';
import { productEffectivePrice } from '../productPricing';
import { isOutOfStock, normalizeStockQty } from '../cart/cartUtils';
import { sortProducts } from '../catalog/ProductListingToolbar';
import { buildCategoryGroupCards } from '../catalogGroupUtils';
import {
  buildAdminGiftGroupOptions,
  buildRecipientOptions,
  findAdminGiftGroup,
  productMatchesAdminGiftGroup,
  productMatchesGroupFilter,
  resolveRecipient,
  usesAdminGiftGroups,
} from './giftFinderGroups';
import { productGiftTags } from '../productGiftTags';

export function getGiftFinderSteps(config = {}) {
  const steps = [];
  if (config.show_recipient_step && (config.recipients || []).length > 0) {
    steps.push('recipient');
  }
  steps.push('collection');
  if (config.show_group_step !== false) steps.push('group');
  if (config.show_budget_step !== false) steps.push('budget');
  steps.push('results');
  return steps;
}

export const GIFT_FINDER_STEPS = getGiftFinderSteps({});

/** Optional icons for known slugs; anything else gets a default. */
const COLLECTION_ICONS = {
  rakhi: '🎀',
  lumba: '✨',
  bracelet: '💫',
  bracelets: '💫',
};

const DEFAULT_COLLECTION_ICON = '🛍️';

export const GIFT_BUDGET_OPTIONS = [
  { id: 'any', label: 'Any budget', hint: 'All price ranges' },
  { id: 'under-300', label: 'Under ₹300', hint: 'Affordable picks' },
  { id: '300-500', label: '₹300 – ₹500', hint: 'Mid-range' },
  { id: '500-1000', label: '₹500 – ₹1,000', hint: 'Gift-worthy' },
  { id: '1000-plus', label: '₹1,000+', hint: 'Premium pieces' },
];

const BUDGET_RANGES = {
  'under-300': { min: 1, max: 299.99 },
  '300-500': { min: 300, max: 500 },
  '500-1000': { min: 500.01, max: 1000 },
  '1000-plus': { min: 1000.01, max: Infinity },
};

export function productMatchesGroup(product, groupKey) {
  const g = String(groupKey || '').trim().toLowerCase();
  if (!g || g === 'any') return true;
  const typ = String(product.type || '').trim().toLowerCase();
  const sub = String(product.subcategory || '').trim().toLowerCase();
  return typ === g || sub === g;
}

function groupLabel(slug) {
  return formatDisplayTitle(String(slug || '').replace(/-/g, ' '));
}

/** Products in scope for the current collection choice. */
export function productsForCollection(allProducts, categorySlug) {
  const cat = String(categorySlug || 'any').trim().toLowerCase();
  const list = Array.isArray(allProducts) ? allProducts : [];
  if (!cat || cat === 'any') return list;
  return list.filter((p) => String(p.category || '').trim().toLowerCase() === cat);
}

/** Count products per category slug. */
export function productCountByCategory(allProducts) {
  const counts = new Map();
  for (const p of allProducts || []) {
    const cat = String(p.category || '').trim().toLowerCase();
    if (!cat) continue;
    counts.set(cat, (counts.get(cat) || 0) + 1);
  }
  return counts;
}

/**
 * Collection step options from live admin categories (not hardcoded rakhi/lumba).
 * @param {Array<{ slug: string, name?: string, sort_order?: number, is_visible?: boolean }>} categories
 * @param {Map<string, number>} countByCategory
 */
export function buildCollectionOptions(categories, countByCategory) {
  const sorted = [...(categories || [])]
    .filter((c) => c && c.slug && c.is_visible !== false)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.slug.localeCompare(b.slug));

  const options = [
    {
      id: 'any',
      label: 'All collections',
      hint: 'Browse everything in the shop',
      icon: '🎁',
    },
  ];

  for (const c of sorted) {
    const slug = String(c.slug).trim().toLowerCase();
    const n = countByCategory.get(slug) || 0;
    if (n === 0) continue;
    options.push({
      id: slug,
      label: formatDisplayTitle(c.name || c.slug),
      hint: `${n} design${n === 1 ? '' : 's'}`,
      icon: COLLECTION_ICONS[slug] || DEFAULT_COLLECTION_ICON,
    });
  }

  return options;
}

function countProductsForGroup(products, groupKey, config) {
  return (products || []).filter((p) => productMatchesGroupFilter(p, groupKey, config)).length;
}

/**
 * Type/style step options from product gift_tags (v2).
 * @param {object[]} scopedProducts
 */
export function buildTagGroupOptions(scopedProducts) {
  const counts = new Map();
  for (const p of scopedProducts || []) {
    for (const tag of productGiftTags(p)) {
      counts.set(tag, (counts.get(tag) || 0) + 1);
    }
  }

  const options = [
    {
      id: 'any',
      label: 'All styles',
      hint: 'Every tagged design in this collection',
      icon: '✨',
    },
  ];

  [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .forEach(([slug, n]) => {
      options.push({
        id: slug,
        label: groupLabel(slug),
        hint: `${n} design${n === 1 ? '' : 's'}`,
        icon: '🏷️',
      });
    });

  return options;
}

/**
 * Group options from Admin taxonomy (subcategories + types) plus any slugs on products.
 * Only lists groups that have at least one product in the current collection scope.
 */
export function buildGroupOptions(scopedProducts, { categoryMeta, categories, config } = {}) {
  if (usesAdminGiftGroups(config)) {
    return buildAdminGiftGroupOptions(scopedProducts, config.gift_groups);
  }

  if (config?.use_product_tags !== false) {
    const tagOptions = buildTagGroupOptions(scopedProducts);
    if (tagOptions.length > 1) {
      return tagOptions;
    }
  }

  const labels = new Map();

  const addCardsFromMeta = (meta, productsInCat) => {
    if (!meta) return;
    for (const card of buildCategoryGroupCards(meta, productsInCat)) {
      const slug = String(card.key || '').trim().toLowerCase();
      if (!slug || slug === 'all') continue;
      if (!labels.has(slug)) labels.set(slug, card.label);
    }
  };

  if (categoryMeta) {
    addCardsFromMeta(categoryMeta, scopedProducts);
  } else if (Array.isArray(categories)) {
    for (const cat of categories) {
      if (!cat?.slug || cat.is_visible === false) continue;
      const catSlug = String(cat.slug).trim().toLowerCase();
      const productsInCat = (scopedProducts || []).filter(
        (p) => String(p.category || '').trim().toLowerCase() === catSlug
      );
      addCardsFromMeta(cat, productsInCat);
    }
  }

  for (const p of scopedProducts || []) {
    for (const raw of [p.type, p.subcategory]) {
      const slug = String(raw || '').trim().toLowerCase();
      if (!slug || slug === 'all' || labels.has(slug)) continue;
      labels.set(slug, groupLabel(slug));
    }
  }

  const options = [
    {
      id: 'any',
      label: 'All types',
      hint: 'Show every design in this collection',
      icon: '✨',
    },
  ];

  [...labels.entries()]
    .map(([slug, label]) => ({ slug, label, n: countProductsForGroup(scopedProducts, slug, config) }))
    .filter((row) => row.n > 0)
    .sort((a, b) => b.n - a.n || a.label.localeCompare(b.label))
    .forEach(({ slug, label, n }) => {
      options.push({
        id: slug,
        label,
        hint: `${n} design${n === 1 ? '' : 's'}`,
        icon: '·',
      });
    });

  return options;
}

export function matchesGiftBudget(product, budgetId) {
  const id = String(budgetId || 'any').trim();
  if (!id || id === 'any') return true;
  const range = BUDGET_RANGES[id];
  if (!range) return true;
  const price = productEffectivePrice(product);
  if (price <= 0) return false;
  return price >= range.min && price <= range.max;
}

function filterGiftFinderInStock(list, inStockOnly) {
  if (!inStockOnly) return list;
  return list.filter((p) => !isOutOfStock(normalizeStockQty(p.stock_qty)));
}

export function filterGiftFinderProducts(
  allProducts,
  { category, group, budget, recipient, inStockOnly, sort, config }
) {
  let list = productsForCollection(allProducts, category);

  const rec = config ? resolveRecipient(config, recipient) : null;
  if (rec) {
    if (rec.category && rec.category !== 'any') {
      list = list.filter((p) => String(p.category || '').trim().toLowerCase() === rec.category);
    }
    if (rec.group_ids?.length) {
      const defs = rec.group_ids.map((gid) => findAdminGiftGroup(config, gid)).filter(Boolean);
      if (defs.length) {
        list = list.filter((p) => defs.some((def) => productMatchesAdminGiftGroup(p, def)));
      }
    } else if (rec.group && rec.group !== 'any') {
      list = list.filter((p) => productMatchesGroupFilter(p, rec.group, config));
    }
  }

  const grp = String(group || 'any').trim().toLowerCase();
  if (grp && grp !== 'any') {
    list = list.filter((p) => productMatchesGroupFilter(p, grp, config));
  }

  const budgetKey = String(budget || 'any').trim().toLowerCase() || 'any';
  if (budgetKey !== 'any') {
    list = list.filter((p) => matchesGiftBudget(p, budgetKey));
  }

  list = filterGiftFinderInStock(list, inStockOnly);
  list = sortProducts(list, sort || 'price-asc');

  return list;
}

export function giftFinderSummary({ category, group, budget, recipient }, optionSets = {}) {
  const rec = (optionSets.recipientOptions || []).find((o) => o.id === recipient);
  const col = (optionSets.collectionOptions || []).find((o) => o.id === category);
  const grp = (optionSets.groupOptions || []).find((o) => o.id === group);
  const bud = GIFT_BUDGET_OPTIONS.find((o) => o.id === budget);
  const parts = [
    rec && rec.id !== 'any' ? rec.label : null,
    col && col.id !== 'any' ? col.label : null,
    grp && grp.id !== 'any' ? grp.label : null,
    bud && bud.id !== 'any' ? bud.label : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'All collections';
}

export { buildRecipientOptions };

export function parseGiftFinderRecipient(searchParams) {
  const id = String(searchParams.get('recipient') || 'any').trim().toLowerCase() || 'any';
  return id;
}

const LEGACY_STEP_MAP = {
  style: 'budget',
};

export function parseGiftFinderStep(raw, config = {}) {
  const steps = getGiftFinderSteps(config);
  const s = String(raw || '').trim().toLowerCase();
  if (!s) return steps[0] || 'collection';
  if (steps.includes(s)) return s;
  if (s === 'recipient' && !steps.includes('recipient')) {
    return steps.includes('collection') ? 'collection' : steps[0] || 'collection';
  }
  if (LEGACY_STEP_MAP[s]) {
    const mapped = LEGACY_STEP_MAP[s];
    if (mapped === 'budget' && config.show_budget_step === false) {
      return 'results';
    }
    if (mapped === 'budget' || steps.includes(mapped)) return mapped;
  }
  if (s === 'group' && config.show_group_step === false) {
    return config.show_budget_step !== false ? 'budget' : 'results';
  }
  if (s === 'budget' && config.show_budget_step === false) return 'results';
  return 'collection';
}

/** Legacy recipient slugs from an older wizard — never valid collection/group keys. */
export const LEGACY_GIFT_FINDER_SLUGS = new Set([
  'brother',
  'sister',
  'bhabhi',
  'kids',
  'newborn',
  'pet',
  'couple',
  'family',
]);

export function isLegacyGiftFinderSlug(slug, config) {
  const s = String(slug || '').trim().toLowerCase();
  if (!s) return false;
  if ((config?.recipients || []).some((r) => String(r.id).toLowerCase() === s)) return false;
  if ((config?.gift_groups || []).some((g) => String(g.id).toLowerCase() === s)) return false;
  return LEGACY_GIFT_FINDER_SLUGS.has(s);
}

/** Read category from URL; ignore orphan legacy slugs unless configured as recipient id. */
export function parseGiftFinderCategory(searchParams, config) {
  const cat = String(searchParams.get('category') || '').trim().toLowerCase();
  if (cat && !isLegacyGiftFinderSlug(cat, config)) return cat;
  return 'any';
}
